import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import { UserContext } from "app/contexts/UserContext";
import { currency_symbol } from "app/routes/Billing/NewOffering/utils";
import { cls } from "app/utils";
import { Spinner } from "app/components/Loading";
import styles from "./styles.module.scss";

interface OfferingProduct {
  id: number;
  title: string;
  description: string;
  productType: "product" | "subscription";
  price: number;
  currency: string;
  enabled: boolean;
  recurring: string | null;
}

interface IProps {
  selectedOfferingId?: string;
  onSelect?: (_offeringId: string | null) => void;
}

const AssignPackageConfig = ({ selectedOfferingId, onSelect }: IProps) => {
  const { t } = useTranslation();
  const { facilityId } = useContext(UserContext);

  const [offerings, setOfferings] = useState<OfferingProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOffering, setSelectedOffering] =
    useState<OfferingProduct | null>(null);
  const selectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!facilityId) {
      return undefined;
    }

    const abortController = new AbortController();
    setLoading(true);

    schedulingFetchGet(
      `/facilities/${facilityId}/stripe_products`,
      {},
      {},
      abortController.signal,
    )
      .then((data: OfferingProduct[]) => {
        if (data && Array.isArray(data)) {
          setOfferings(data.filter((p) => p.enabled));
        }
      })
      .catch((error) => {
        if (error?.name !== "AbortError") {
          console.error("Failed to fetch offerings:", error);
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [facilityId]);

  // Sync selected offering from workflow prop whenever offerings load or prop changes
  useEffect(() => {
    if (offerings.length === 0) {
      return;
    }
    if (!selectedOfferingId) {
      setSelectedOffering(null);
    } else {
      const match = offerings.find(
        (o) => String(o.id) === String(selectedOfferingId),
      );
      setSelectedOffering(match || null);
    }
  }, [selectedOfferingId, offerings]);

  const handleSelect = (offering: OfferingProduct) => {
    setSelectedOffering(offering);
    setIsOpen(false);
    onSelect?.(String(offering.id));
  };

  const handleRemove = () => {
    setSelectedOffering(null);
    onSelect?.(null);
  };

  const handleBlur = (e: React.FocusEvent) => {
    if (
      selectRef.current &&
      !selectRef.current.contains(e.relatedTarget as Node)
    ) {
      setIsOpen(false);
    }
  };

  const getTypeLabel = (productType: string) => {
    return productType === "product" ? t("Package") : t("Subscription");
  };

  const formatPrice = (price: number, currency: string) => {
    const symbol = currency_symbol[currency] || currency;
    return `${symbol}${price}`;
  };

  const getBillingLabel = (offering: OfferingProduct) => {
    if (offering.recurring) {
      return t("Recurring billing");
    }
    return t("One time payment");
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <p className={styles.description}>
        {t(
          "You can assign a package or subscription to this workflow at no cost for athletes.",
        )}
      </p>

      <span className={styles.label}>{t("Package or Subscription")}</span>

      <div
        className={styles.select_wrapper}
        ref={selectRef}
        onBlur={handleBlur}
      >
        <button
          className={cls(styles.select_btn, { [styles.open]: isOpen })}
          onClick={() => setIsOpen(!isOpen)}
          type="button"
        >
          <span
            className={cls({
              [styles.placeholder]: !selectedOffering,
            })}
          >
            {selectedOffering ? selectedOffering.title : t("Select")}
          </span>
          <i
            className={cls("ico", "ico-chevron", styles.chevron, {
              [styles.chevron_open]: isOpen,
            })}
          />
        </button>

        {isOpen && (
          <div className={styles.options_list}>
            {loading ? (
              <div className={styles.option_item}>
                <span className={styles.option_name}>{t("Loading...")}</span>
              </div>
            ) : offerings.length > 0 ? (
              offerings.map((offering) => (
                <div
                  key={offering.id}
                  className={styles.option_item}
                  onMouseDown={() => handleSelect(offering)}
                >
                  <div className={styles.option_name}>{offering.title}</div>
                  <div className={styles.option_type}>
                    {getTypeLabel(offering.productType)}
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.option_item}>
                <span className={styles.option_name}>
                  {t("No offerings available")}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {selectedOffering ? (
        <div className={styles.package_card}>
          <button
            className={styles.remove_btn}
            onClick={handleRemove}
            type="button"
            aria-label="remove"
          />

          <span
            className={cls(styles.badge, {
              [styles.badge_subscription]:
                selectedOffering.productType === "subscription",
              [styles.badge_package]:
                selectedOffering.productType === "product",
            })}
          >
            {t(getTypeLabel(selectedOffering.productType).toUpperCase())}
          </span>

          <h3 className={styles.card_title}>{selectedOffering.title}</h3>
          {selectedOffering.description && (
            <p className={styles.card_description}>
              {selectedOffering.description}
            </p>
          )}

          <div className={styles.card_footer}>
            <div className={styles.price_row}>
              <span className={styles.price}>
                {formatPrice(0, selectedOffering.currency)}
              </span>
              {selectedOffering.price > 0 && (
                <span className={styles.original_price}>
                  {formatPrice(
                    selectedOffering.price,
                    selectedOffering.currency,
                  )}
                </span>
              )}
            </div>
            <div className={styles.billing_type}>
              {getBillingLabel(selectedOffering)}
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.empty_state}>
          <i className={styles.empty_icon + " ico ico-dollar-circle"} />
          <h3 className={styles.empty_title}>
            {t("No Packages and Subscriptions yet")}
          </h3>
          <p className={styles.empty_subtitle}>
            {t("Add a product you want to attach to this workflow")}
          </p>
        </div>
      )}
    </div>
  );
};

export default AssignPackageConfig;
